import { useEffect, useMemo, useState } from 'react';
import { Typography } from '@mui/material';

import { DYDX_TOKEN } from 'src/constants/tokens';
import { useCosmosBalanceSWR } from 'src/hooks/SWR/cosmos/useCosmosBalanceSWR';
import { useCurrentAccount } from 'src/hooks/useAccount';
import { useCurrentTab } from 'src/hooks/useCurrentTab';
import { useCurrentWallet } from 'src/hooks/useCurrentWallet';
import { useWindowSize } from 'src/hooks/useWindowSize';
import { gt } from 'src/utils/number';
import { shortAddress } from 'src/utils/string';

import {
  CloseIconButton,
  Container,
  GradientTooltipBodyContainer,
  GradientTooltipContainer,
  IconContainer,
  RightContainer,
  StyledReverseGradientIconTextButton,
  TabButtonContainer,
  TabButtonItemContainer,
} from './styled';
import MnemonicPopover from '../MnemonicPopover';
import MobileMnemonicPopover from '../MobileMnemonicPopover';
import TabButton from '../TabButton';
import WalletPopover from '../WalletPopover';

import { ReactComponent as CloseIcon } from 'src/assets/svg/Close.svg';
import { ReactComponent as CosmostationWalletIcon } from 'src/assets/svg/CosmostationWallet.svg';
import { ReactComponent as GradientTooltipIcon } from 'src/assets/svg/GradientTooltip.svg';
import { ReactComponent as MetamaskWalletIcon } from 'src/assets/svg/Metamask.svg';
import { ReactComponent as MintscanIcon } from 'src/assets/svg/MintscanSwap.svg';
import { ReactComponent as WalletIcon } from 'src/assets/svg/Wallet.svg';

type HeaderProps = {
  onClickConnectWallet: (isOpen: boolean) => void;
  onClickDisconnect: () => void;
};

export default function Header({ onClickConnectWallet, onClickDisconnect }: HeaderProps) {
  const windowSize = useWindowSize();

  const { currentWallet } = useCurrentWallet();
  const { currentAccount } = useCurrentAccount();
  const { currentTab, setCurrentTab } = useCurrentTab();

  const [isOpenStakingTooltip, setIsOpenStakingTooltip] = useState(false);
  const [isClickedStakingTooltip, setIsClickedStakingTooltip] = useState(false);

  const [isOpenMnemonicPopover, setIsOpenMnemonicPopover] = useState(false);
  const [isOpenMobileMnemonicPopover, setIsOpenMobileMnemonicPopover] = useState(false);

  const cosmosBalance = useCosmosBalanceSWR(currentAccount.cosmosAddress);

  const dydxNativeCoinAmount = useMemo(
    () => cosmosBalance.data?.balances?.find((item) => item.denom === DYDX_TOKEN.denom)?.amount || '0',
    [cosmosBalance.data?.balances],
  );

  const halfWindowSize = useMemo(() => windowSize.width / 2, [windowSize.width]);

  const halfWindowHeight = useMemo(() => windowSize.height / 2, [windowSize.height]);

  const [ethereumPopoverAnchorEl, setEthereumPopoverAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isOpenEthereumPopover = Boolean(ethereumPopoverAnchorEl);

  const walletIcon = useMemo(
    () => (currentWallet?.walletType === 'Cosmostation' ? <CosmostationWalletIcon /> : <MetamaskWalletIcon />),
    [currentWallet?.walletType],
  );

  const shortAccAddress = useMemo(() => shortAddress(currentAccount.cosmosAddress, 6), [currentAccount.cosmosAddress]);

  useEffect(() => {
    if (currentAccount.accAddress && cosmosBalance.data && gt(dydxNativeCoinAmount, '0') && !isClickedStakingTooltip) {
      setIsOpenStakingTooltip(true);
    }
  }, [cosmosBalance.data, currentAccount.accAddress, dydxNativeCoinAmount, isClickedStakingTooltip]);

  useEffect(() => {
    if (!currentAccount.accAddress) {
      setIsOpenStakingTooltip(false);
    }
  }, [currentAccount.accAddress]);

  return (
    <Container>
      <MintscanIcon />
      <TabButtonContainer>
        <TabButton
          text="BRIDGE"
          isActive={currentTab.tabIndex === 0}
          onClick={() => {
            setCurrentTab({ tabIndex: 0 });
          }}
        />
        <TabButtonItemContainer>
          <TabButton
            text="STAKING"
            isActive={currentTab.tabIndex === 1}
            onClick={() => {
              setCurrentTab({ tabIndex: 1 });
            }}
          />
          {isOpenStakingTooltip && (
            <GradientTooltipContainer>
              <GradientTooltipIcon />
              <GradientTooltipBodyContainer>
                <Typography variant="h11">{`You already have DYDX.\nStart staking here!`}</Typography>
                <CloseIconButton
                  onClick={() => {
                    setIsOpenStakingTooltip(false);
                    setIsClickedStakingTooltip(true);
                  }}
                >
                  <CloseIcon />
                </CloseIconButton>
              </GradientTooltipBodyContainer>
            </GradientTooltipContainer>
          )}
        </TabButtonItemContainer>
        <TabButton
          text="TRANSFER"
          isActive={currentTab.tabIndex === 2}
          onClick={() => {
            setCurrentTab({ tabIndex: 2 });
          }}
        />
        <TabButton
          text="GOVERNANCE"
          isActive={currentTab.tabIndex === 3}
          onClick={() => {
            setCurrentTab({ tabIndex: 3 });
          }}
        />
      </TabButtonContainer>

      <RightContainer>
        {/* NOTE Delete GUIDE Button */}
        {/* <IconTextButton  onClick={() => window.open('https://medium.com/cosmostation/dydx-migration-guide-2af37f95def5')} icon={<Doc />} text="GUIDE" /> */}

        {currentWallet.name && shortAccAddress ? (
          <StyledReverseGradientIconTextButton
            icon={<IconContainer>{walletIcon}</IconContainer>}
            text={shortAccAddress}
            onClick={(event) => setEthereumPopoverAnchorEl(event.currentTarget)}
          />
        ) : (
          <StyledReverseGradientIconTextButton
            icon={<WalletIcon />}
            text="Connect Wallet"
            onClick={() => {
              onClickConnectWallet(true);
            }}
          />
        )}
      </RightContainer>

      <MnemonicPopover
        marginThreshold={0}
        anchorReference="anchorPosition"
        anchorPosition={{ top: halfWindowHeight, left: halfWindowSize }}
        open={isOpenMnemonicPopover}
        onClose={() => setIsOpenMnemonicPopover(false)}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
      />
      <MobileMnemonicPopover
        marginThreshold={0}
        anchorReference="anchorPosition"
        anchorPosition={{ top: halfWindowHeight, left: halfWindowSize }}
        open={isOpenMobileMnemonicPopover}
        onClose={() => setIsOpenMobileMnemonicPopover(false)}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
      />
      <WalletPopover
        marginThreshold={0}
        open={isOpenEthereumPopover}
        onClose={() => setEthereumPopoverAnchorEl(null)}
        onClickMnemonic={(isOpen) => {
          setIsOpenMnemonicPopover(isOpen);
        }}
        onClickMobileMnemonic={(isOpen) => {
          setIsOpenMobileMnemonicPopover(isOpen);
        }}
        onClickDisconnect={onClickDisconnect}
        anchorEl={ethereumPopoverAnchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      />
    </Container>
  );
}
